import Menu from './menu';

import spritesData from '../../data/sprites.json';

import { Opcodes, Packets } from '@kaetram/common/network';

import type Game from '../game';
import type { CoinRankEntry } from '@kaetram/common/types/messages/outgoing';

const IDLE_ROW = 8, // Row in the spritesheet for the front-facing idle pose.
    PODIUM_SIZE = 104, // Canvas render size for podium characters (matches CSS width).
    LIST_SIZE = 48, // Canvas render size for list characters.
    // Lookup of sprite frame dimensions by id (built once from sprites.json).
    SPRITE_DIMENSIONS: { [id: string]: { width: number; height: number } } = {};

for (let sprite of spritesData as { id: string; width: number; height: number }[])
    SPRITE_DIMENSIONS[sprite.id] = { width: sprite.width, height: sprite.height };

/**
 * CoinCTF ranking menu. Requests the ranking from the server whenever it is opened
 * and renders the top three players on a podium (with their character sprite) followed
 * by the remaining players in a list.
 */

export default class CoinRanking extends Menu {
    private podium: HTMLElement = document.querySelector('#coinranking-podium')!;
    private list: HTMLUListElement = document.querySelector('#coinranking-list')!;
    private empty: HTMLElement = document.querySelector('#coinranking-empty')!;

    // Note: we store `game` (not `game.socket`) because MenuController is built
    // BEFORE the socket exists in the Game constructor — capturing the socket
    // here would permanently store `undefined`.
    public constructor(private game: Game) {
        super('#coinranking', '#close-coinranking', '#coinranking-button');
    }

    /**
     * Override to request the latest ranking from the server whenever the menu opens.
     */

    public override show(): void {
        super.show();

        // Show a loading state until the server responds (also makes network
        // failures visible instead of a silent blank panel).
        this.podium.innerHTML = '';
        this.list.innerHTML = '';
        this.empty.textContent = 'Cargando ranking...';
        this.empty.hidden = false;

        this.game.socket.send(Packets.CoinRanking, { opcode: Opcodes.CoinRanking.Request });
    }

    /**
     * Receives the ranking list from the server and renders the podium + list.
     * @param list Array of ranking entries sorted descending by coin count.
     */

    public setRanking(list: CoinRankEntry[]): void {
        this.podium.innerHTML = '';
        this.list.innerHTML = '';

        if (!list || list.length === 0) {
            this.empty.textContent = 'Aún no hay jugadores con CoinCTF.';
            this.empty.hidden = false;
            return;
        }

        this.empty.hidden = true;

        // Always render three slots on the podium in the visual order 2nd - 1st - 3rd,
        // even if there are not yet three players — the missing spots show a placeholder
        // so the podium always reads as a podium.
        let top = list.slice(0, 3),
            podiumOrder = [top[1], top[0], top[2]],
            ranks = [2, 1, 3];

        for (let [i, entry] of podiumOrder.entries())
            if (entry) this.podium.append(this.createPodiumSlot(entry, ranks[i]));
            else this.podium.append(this.createEmptyPodiumSlot(ranks[i]));

        // Remaining players (4th onwards) in the list below.
        for (let i = 3; i < list.length; i++) this.list.append(this.createListRow(list[i], i + 1));
    }

    /**
     * Renders a placeholder slot for a rank that no one has claimed yet.
     * Kept visually consistent with real slots but dimmed and with generic text.
     */

    private createEmptyPodiumSlot(rank: number): HTMLElement {
        let slot = document.createElement('div');
        slot.classList.add('coinranking-podium-slot', `rank-${rank}`, 'is-empty');

        let medal = document.createElement('div');
        medal.classList.add('coinranking-medal', `medal-${rank}`);

        let medalNum = document.createElement('span');
        medalNum.classList.add('coinranking-medal-num');
        medalNum.textContent = `${rank}`;
        medal.append(medalNum);

        let placeholder = document.createElement('div');
        placeholder.classList.add('coinranking-char', 'coinranking-char-empty');
        placeholder.textContent = '?';

        let name = document.createElement('div');
        name.classList.add('coinranking-name');
        name.textContent = '—';

        let meta = document.createElement('div');
        meta.classList.add('coinranking-meta');
        meta.textContent = 'Sin jugador';

        let coins = document.createElement('div');
        coins.classList.add('coinranking-coins');

        let coinsNum = document.createElement('span');
        coinsNum.classList.add('coinranking-coins-num');
        coinsNum.textContent = '—';

        coins.append(coinsNum);

        slot.append(medal, placeholder, name, meta, coins);

        return slot;
    }

    /**
     * Creates a podium slot for one of the top three players.
     * @param entry The ranking entry to display.
     * @param rank The rank (1, 2 or 3) — controls styling and podium height.
     */

    private createPodiumSlot(entry: CoinRankEntry, rank: number): HTMLElement {
        let slot = document.createElement('div');
        slot.classList.add('coinranking-podium-slot', `rank-${rank}`);

        // Medal — circular badge with the rank number inside.
        let medal = document.createElement('div');
        medal.classList.add('coinranking-medal', `medal-${rank}`);

        let medalNum = document.createElement('span');
        medalNum.classList.add('coinranking-medal-num');
        medalNum.textContent = `${rank}`;
        medal.append(medalNum);

        let canvas = document.createElement('canvas');
        canvas.width = PODIUM_SIZE;
        canvas.height = PODIUM_SIZE;
        canvas.classList.add('coinranking-char');
        this.renderCharacter(canvas, entry.appearance);

        let name = document.createElement('div');
        name.classList.add('coinranking-name');
        name.textContent = this.safeName(entry.username);

        let meta = document.createElement('div');
        meta.classList.add('coinranking-meta');
        meta.textContent = this.formatMeta(entry);

        let coins = document.createElement('div');
        coins.classList.add('coinranking-coins');

        let coinsNum = document.createElement('span');
        coinsNum.classList.add('coinranking-coins-num');
        coinsNum.textContent = `${entry.coins}`;

        let coinsLabel = document.createElement('span');
        coinsLabel.classList.add('coinranking-coins-label');
        coinsLabel.textContent = ' CoinCTF';

        coins.append(coinsNum, coinsLabel);

        slot.append(medal, canvas, name, meta, coins);

        return slot;
    }

    /**
     * Creates a list row for a player ranked 4th or lower.
     * @param entry The ranking entry to display.
     * @param position The 1-indexed leaderboard position.
     */

    private createListRow(entry: CoinRankEntry, position: number): HTMLElement {
        let row = document.createElement('li');
        row.classList.add('coinranking-row');

        let pos = document.createElement('span');
        pos.classList.add('coinranking-pos');
        pos.textContent = `${position}`;

        let canvas = document.createElement('canvas');
        canvas.width = LIST_SIZE;
        canvas.height = LIST_SIZE;
        canvas.classList.add('coinranking-char-small');
        this.renderCharacter(canvas, entry.appearance);

        let name = document.createElement('span');
        name.classList.add('coinranking-name');
        name.textContent = this.safeName(entry.username);

        let meta = document.createElement('span');
        meta.classList.add('coinranking-meta');
        meta.textContent = this.formatMeta(entry);

        let coins = document.createElement('span');
        coins.classList.add('coinranking-coins');
        coins.textContent = `${entry.coins}`;

        row.append(pos, canvas, name, meta, coins);

        return row;
    }

    /**
     * Defensive fallback for usernames — if the server sends an empty or whitespace-only
     * string, we display 'Anónimo' so the row still shows something instead of an empty gap.
     */

    private safeName(username: string | undefined): string {
        let trimmed = (username ?? '').trim();
        return trimmed || 'Anónimo';
    }

    /**
     * Formats the secondary line: combat level and (if present) guild name.
     * @param entry The ranking entry.
     * @returns A formatted string, e.g. "Nv. 42 · MiClan".
     */

    private formatMeta(entry: CoinRankEntry): string {
        return `Nv. ${entry.level}${entry.guild ? ` · ${entry.guild}` : ''}`;
    }

    /**
     * Renders the front-facing idle frame of a character's spritesheet onto a canvas.
     * Mirrors the approach used by the character selection screen.
     * @param canvas The canvas element to draw onto.
     * @param appearance The sprite key (e.g. an armour or armour skin key).
     */

    private renderCharacter(canvas: HTMLCanvasElement, appearance: string): void {
        let ctx = canvas.getContext('2d');

        if (!ctx) return;

        ctx.imageSmoothingEnabled = false;

        let size = canvas.width,
            img = new Image(),
            // Placeholder drawn when the sprite fails to load or the row for the idle
            // pose is missing. Keeps the layout stable and gives visual feedback.
            drawPlaceholder = (): void => {
                ctx!.clearRect(0, 0, size, size);
                ctx!.fillStyle = 'rgba(0, 0, 0, 0.05)';
                ctx!.fillRect(0, 0, size, size);
                ctx!.strokeStyle = 'rgba(0, 0, 0, 0.15)';
                ctx!.lineWidth = 2;
                ctx!.strokeRect(2, 2, size - 4, size - 4);
            };

        img.addEventListener('load', () => {
            let dims = SPRITE_DIMENSIONS[`player/${appearance}`],
                frameWidth: number,
                frameHeight: number;

            if (dims) {
                frameWidth = dims.width;
                frameHeight = dims.height;
            } else {
                frameWidth = img.naturalWidth;
                let guessedRows = Math.max(1, Math.round(img.naturalHeight / img.naturalWidth));
                frameHeight = Math.floor(img.naturalHeight / guessedRows);
            }

            // Pick the idle row. If it does not exist for this sheet (mobs / short
            // sheets), fall back to row 0 so we always show something.
            let idleRow = IDLE_ROW;
            if (idleRow * frameHeight >= img.naturalHeight) idleRow = 0;

            // Letterbox: scale by the tighter axis so a non-square sprite stays
            // fully inside the canvas, centered, without overflowing into the name
            // element below.
            let scale = Math.min(size / frameWidth, size / frameHeight),
                drawWidth = frameWidth * scale,
                drawHeight = frameHeight * scale,
                drawX = Math.floor((size - drawWidth) / 2),
                drawY = Math.floor((size - drawHeight) / 2);

            ctx!.clearRect(0, 0, size, size);
            ctx!.imageSmoothingEnabled = false;
            ctx!.drawImage(
                img,
                0,
                idleRow * frameHeight,
                frameWidth,
                frameHeight,
                drawX,
                drawY,
                drawWidth,
                drawHeight
            );
        });

        img.addEventListener('error', drawPlaceholder);

        img.src = `/img/sprites/player/${appearance}.png`;
    }
}
